import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ShieldCheck, Clock, HeartHandshake, MapPin, Video, Phone, Stethoscope, Scale, GraduationCap, Landmark, Building2, Users, Star, Mail, CheckCircle2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLines, FadeIn } from "../components/Motion";
import { CtaBand } from "../components/CtaBand";
import { useLang } from "../i18n/LanguageContext";
import { CLIENTS, businessSchema, LOGO_URL, SITE_URL, CONTACT } from "../data/content";

const HERO_IMG = "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const Hero = () => {
  const { t } = useLang();
  const h = t.home;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yCircle = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} className="relative overflow-hidden texture-grain" data-testid="home-hero">
      <motion.div style={{ y: yCircle }} className="absolute -top-32 -right-40 h-[34rem] w-[34rem] rounded-full bg-periwinkle/25" aria-hidden="true" />
      <div className="absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-sage/15" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-16 grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <FadeIn delay={0.05}>
            <p className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-periwinkle-deep mb-8">{h.kicker}</p>
          </FadeIn>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.02] tracking-tight text-ink">
            <MaskedLines lines={h.heroLines} />
          </h1>
          <FadeIn delay={0.7}>
            <p className="mt-8 text-base sm:text-lg text-muted2 leading-relaxed max-w-xl">{h.heroPara}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="hero-quote-btn" className="rounded-full bg-ink text-cream px-8 py-4 font-semibold hover:bg-periwinkle-deep transition-colors duration-300">
                {h.heroQuote}
              </Link>
              <Link to="/contact" data-testid="hero-services-btn" className="rounded-full border border-ink/20 px-8 py-4 font-semibold hover:border-ink transition-colors duration-300">
                {h.heroServices}
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-2 gap-3 max-w-lg">
              {h.badges.map((b) => (
                <div key={b} className="flex items-center gap-2 text-sm text-ink/70">
                  <CheckCircle2 size={16} className="text-sage-deep shrink-0" />
                  {b}
                </div>
              ))}
            </div>
          </FadeIn>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div style={{ y: yImg }} className="relative">
            <FadeIn delay={0.5}>
              <div className="rounded-t-full overflow-hidden border-8 border-white shadow-2xl shadow-periwinkle/20">
                <img src={HERO_IMG} alt={h.heroImgAlt} className="w-full h-[26rem] lg:h-[30rem] object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-8 bg-white rounded-lg shadow-xl px-6 py-4 border border-ink/5">
                <p className="font-serif text-3xl font-semibold text-ink">{h.badgeK}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted2">{h.badgeV}</p>
              </div>
            </FadeIn>
          </motion.div>
        </div>
      </div>

      <FadeIn delay={0.9}>
        <div className="relative border-t border-ink/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 grid grid-cols-2 md:grid-cols-4 gap-8">
            {h.stats.map(([k, v]) => (
              <div key={v}>
                <p className="font-serif text-2xl sm:text-3xl font-semibold text-ink">{k}</p>
                <p className="text-xs text-muted2 mt-1">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </FadeIn>
    </section>
  );
};

const ClientMarquee = () => {
  const { t } = useLang();
  return (
    <section className="bg-white border-y border-ink/5 py-10" data-testid="client-marquee">
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted2 text-center mb-8">{t.home.marqueeLabel}</p>
      <Marquee speed={30} gradient gradientColor="#ffffff" gradientWidth={120} autoFill>
        {CLIENTS.map((c) => (
          <span key={c} className="mx-10 font-serif text-2xl text-ink/40 whitespace-nowrap italic">{c}</span>
        ))}
      </Marquee>
    </section>
  );
};

const WhoWeAre = () => {
  const { t } = useLang();
  const h = t.home;
  const icons = [ShieldCheck, Clock, HeartHandshake];
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="who-we-are-section">
      <div className="grid lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-7">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-periwinkle-deep mb-4">{h.whoKicker}</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-medium leading-[1.08]">{h.whoTitle}</h2>
          <p className="mt-8 text-muted2 leading-relaxed">{h.whoP1}</p>
          <p className="mt-4 text-muted2 leading-relaxed">{h.whoP2}</p>
        </Reveal>
        <div className="lg:col-span-5 space-y-6">
          {h.values.map((v, i) => {
            const Icon = icons[i];
            return (
              <Reveal key={v.t} delay={i * 0.1}>
                <div className="flex gap-5 bg-white border border-ink/10 rounded-lg p-7">
                  <Icon size={22} className="text-periwinkle-deep shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold">{v.t}</h3>
                    <p className="text-sm text-muted2 leading-relaxed mt-1">{v.d}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Manifesto = () => {
  const { t } = useLang();
  const h = t.home;
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24 lg:pb-32" data-testid="manifesto-section">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-periwinkle-deep mb-4">{h.manifestoKicker}</p>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] max-w-2xl">{h.manifestoTitle}</h2>
      </Reveal>
      <div className="mt-16 grid md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
        {h.manifesto.map((m, i) => (
          <Reveal key={m.n} delay={i * 0.12} className="bg-cream p-10 lg:p-14">
            <p className="font-mono text-sm text-periwinkle-deep">{m.n}</p>
            <h3 className="font-serif text-2xl lg:text-3xl font-semibold mt-6">{m.t}</h3>
            <p className="text-sm text-muted2 leading-relaxed mt-4">{m.d}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const ServicesGrid = () => {
  const { t, data } = useLang();
  const h = t.home;
  return (
    <section className="bg-white border-y border-ink/5 texture-grain" data-testid="home-services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <Reveal className="mb-16">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-periwinkle-deep mb-4">{h.servicesKicker}</p>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05]">{h.servicesTitle}</h2>
            </div>
            <Link to="/services" data-testid="home-all-services-link" className="link-underline font-semibold text-periwinkle-deep">{h.allServices}</Link>
          </div>
          <p className="mt-6 text-muted2 leading-relaxed max-w-2xl">{h.servicesIntro}</p>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6">
          {data.SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.08} className={i === 0 ? "md:col-span-2" : ""}>
              <Link
                to={`/services/${s.slug}`}
                data-testid={`home-service-card-${s.slug}`}
                className="group flex flex-col sm:flex-row gap-6 bg-cream border border-ink/10 rounded-lg p-8 lg:p-10 hover:border-periwinkle transition-colors duration-300 h-full"
              >
                <p className="font-mono text-sm text-periwinkle-deep">{s.num}</p>
                <div className="flex-1">
                  <h3 className="font-serif text-2xl lg:text-3xl font-semibold flex items-center gap-3">
                    {s.title}
                    <ArrowUpRight size={20} className="text-periwinkle-deep opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </h3>
                  <p className="text-sm text-muted2 leading-relaxed mt-3 max-w-xl">{s.short}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhyChoose = () => {
  const { t, data } = useLang();
  const h = t.home;
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="features-section">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-periwinkle-deep mb-4">{h.whyKicker}</p>
        <h2 className="font-serif text-4xl sm:text-5xl font-medium leading-[1.05] max-w-3xl">{h.whyTitle}</h2>
        <p className="mt-6 text-muted2 leading-relaxed max-w-2xl">{h.whyIntro}</p>
      </Reveal>
      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
        {data.FEATURES.map((f, i) => (
          <Reveal key={f.title} delay={i * 0.07}>
            <div className="h-1 w-10 bg-sage mb-5" />
            <h3 className="font-semibold text-lg">{f.title}</h3>
            <p className="text-sm text-muted2 leading-relaxed mt-2">{f.desc}</p>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15} className="mt-20 bg-ink texture-grain rounded-lg p-10 lg:p-14">
        <h3 className="font-serif text-3xl text-cream font-semibold">{h.numbersTitle}</h3>
        <p className="text-cream/60 text-sm mt-2">{h.numbersIntro}</p>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {h.stats.map(([k, v]) => (
            <div key={v}>
              <p className="font-serif text-4xl font-semibold text-periwinkle-light">{k}</p>
              <p className="text-xs text-cream/60 mt-2 leading-relaxed">{v}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </section>
  );
};

const Industries = () => {
  const { t } = useLang();
  const h = t.home;
  const icons = [Stethoscope, Scale, GraduationCap, Landmark, Building2, Users];
  return (
    <section className="bg-periwinkle/10 border-y border-ink/5" data-testid="industries-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-periwinkle-deep mb-4">{h.industriesKicker}</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-medium leading-[1.05]">{h.industriesTitle}</h2>
          <p className="mt-6 text-muted2 leading-relaxed max-w-2xl">{h.industriesIntro}</p>
        </Reveal>
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {h.industries.map((ind, i) => {
            const Icon = icons[i];
            return (
              <Reveal key={ind.t} delay={i * 0.07}>
                <div className="bg-white border border-ink/10 rounded-lg p-8 h-full">
                  <Icon size={24} className="text-periwinkle-deep" />
                  <h3 className="font-serif text-2xl font-semibold mt-5">{ind.t}</h3>
                  <p className="text-sm text-muted2 leading-relaxed mt-3">{ind.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Delivery = () => {
  const { t } = useLang();
  const h = t.home;
  const icons = [MapPin, Video, Phone];
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="delivery-section">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-periwinkle-deep mb-4">{h.deliveryKicker}</p>
        <h2 className="font-serif text-4xl sm:text-5xl font-medium leading-[1.05]">{h.deliveryTitle}</h2>
        <p className="mt-6 text-muted2 leading-relaxed max-w-2xl">{h.deliveryIntro}</p>
      </Reveal>
      <div className="mt-16 grid md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
        {h.delivery.map((d, i) => {
          const Icon = icons[i];
          return (
            <Reveal key={d.t} delay={i * 0.1} className="bg-cream p-10">
              <Icon size={24} className="text-sage-deep" />
              <h3 className="font-serif text-2xl font-semibold mt-6">{d.t}</h3>
              <p className="text-sm text-muted2 leading-relaxed mt-3">{d.d}</p>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

const Testimonials = () => {
  const { t } = useLang();
  const h = t.home;
  return (
    <section className="bg-white border-y border-ink/5 texture-grain" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-periwinkle-deep mb-4">{h.testimonialsKicker}</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-medium leading-[1.05]">{h.testimonialsTitle}</h2>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {h.testimonials.map((tm, i) => (
            <Reveal key={tm.name} delay={i * 0.1}>
              <figure className="bg-cream border border-ink/10 rounded-lg p-8 h-full flex flex-col">
                <div className="flex gap-1 text-periwinkle-deep">
                  {[...Array(5)].map((_, s) => <Star key={s} size={14} fill="currentColor" />)}
                </div>
                <blockquote className="mt-5 text-sm text-ink/80 leading-relaxed flex-1 italic">"{tm.quote}"</blockquote>
                <figcaption className="mt-6 pt-5 border-t border-ink/10">
                  <p className="font-semibold text-sm">{tm.name}</p>
                  <p className="text-xs text-muted2 mt-0.5">{tm.role}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const HomeFaq = () => {
  const { t, data } = useLang();
  const h = t.home;
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="home-faq-section">
      <div className="grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-periwinkle-deep mb-4">{h.faqKicker}</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-medium leading-[1.05]">{h.faqTitle}</h2>
            <p className="mt-4 text-muted2">{h.faqIntro}</p>
            <Accordion type="single" collapsible className="mt-10 border-t border-ink/10">
              {data.FAQS.slice(0, 6).map((f, i) => (
                <AccordionItem key={i} value={`hfaq-${i}`} data-testid={`home-faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-semibold hover:text-periwinkle-deep">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted2 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <Link to="/faq" data-testid="home-all-faqs-link" className="inline-block mt-8 link-underline font-semibold text-periwinkle-deep">{h.allFaqs}</Link>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="bg-ink texture-grain rounded-lg p-10 sticky top-28">
            <h3 className="font-serif text-3xl text-cream font-semibold">{h.stillH}</h3>
            <p className="text-cream/60 text-sm leading-relaxed mt-4">{h.stillP}</p>
            <div className="mt-8 space-y-4">
              <a href={CONTACT.phoneHref} data-testid="home-phone-link" className="flex items-center gap-3 text-cream hover:text-periwinkle-light transition-colors duration-300">
                <Phone size={18} className="text-periwinkle-light" /> {CONTACT.phone}
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="home-email-link" className="flex items-center gap-3 text-cream hover:text-periwinkle-light transition-colors duration-300 break-all">
                <Mail size={18} className="text-periwinkle-light" /> {CONTACT.email}
              </a>
              <p className="flex items-center gap-3 text-cream/70">
                <MapPin size={18} className="text-periwinkle-light" /> {CONTACT.location}
              </p>
            </div>
            <Link to="/contact" data-testid="home-still-contact-btn" className="block mt-8 rounded-full bg-periwinkle text-ink px-8 py-4 font-semibold text-center hover:bg-periwinkle-light transition-colors duration-300">
              {h.contactBtn}
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const AboutTeaser = () => {
  const { t } = useLang();
  const h = t.home;
  return (
    <section className="bg-periwinkle/10 border-y border-ink/5" data-testid="about-teaser">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-4">
          <div className="rounded-t-full overflow-hidden border-8 border-white shadow-xl max-w-xs">
            <img src={LOGO_URL} alt="SG Language Services watercolor logo" className="w-full aspect-[4/5] object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-8">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-periwinkle-deep mb-4">{h.aboutKicker}</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-medium leading-[1.1]">{h.aboutTitle}</h2>
          <p className="mt-6 text-muted2 leading-relaxed max-w-2xl">{h.aboutPara}</p>
          <Link to="/about" data-testid="about-teaser-link" className="inline-block mt-8 link-underline font-semibold text-periwinkle-deep">{h.aboutLink}</Link>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  const { t, data } = useLang();
  const homeSchema = {
    "@context": "https://schema.org",
    "@graph": [
      businessSchema,
      { "@type": "WebSite", "@id": `${SITE_URL}#website`, url: SITE_URL, name: "SG Language Services LLC", inLanguage: ["en-US", "es"] },
      {
        "@type": "FAQPage",
        mainEntity: data.FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a }
        }))
      }
    ]
  };

  return (
    <>
      <Seo title={t.home.seoTitle} description={t.home.seoDesc} path="/" schema={homeSchema} />
      <Hero />
      <ClientMarquee />
      <WhoWeAre />
      <Manifesto />
      <ServicesGrid />
      <WhyChoose />
      <Industries />
      <Delivery />
      <Testimonials />
      <HomeFaq />
      <AboutTeaser />
      <CtaBand />
    </>
  );
}
